import classNames from 'classnames'
import React, { useState } from 'react'
import ArrowIcon from '../../assets/icons/arrow.svg'
import { useTranslations } from '../../hooks/useTranslations'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { AccordionButton } from '../Accordion/AccordionButton'
import { Collapse } from '../Collapse'
import { MarketSection } from '../MarketSection'
import { Paragraph } from '../Paragraph'
import { Attachment } from '../ReportAttachmentsList'

export interface KeyDocumentRow {
  cells: (string | undefined | null)[]
  attachments?: Attachment[]
  secondaryAttachments?: Attachment[]
}

export interface KeyDocumentsProps {
  downloadsGaTrackingId?: string
  id?: string
  rows?: KeyDocumentRow[]
  tableHeadings?: string[]
  text?: string
  title?: string
}

export const KeyDocuments = ({
  downloadsGaTrackingId,
  id,
  rows,
  tableHeadings,
  text,
  title,
}: KeyDocumentsProps) => {
  const [expanded, setExpanded] = useState(false)
  const translations = useTranslations()

  return (
    <MarketSection id={id}>
      <div
        className={classNames(
          'flex flex-col gap-30 overflow-hidden',
          'border border-neutral-lighter rounded-15',
          'col-span-full',
          'sm:col-start-2 sm:col-span-4',
          'md:col-start-1 md:col-span-full',
          'lg:col-start-2 lg:col-span-6 lg:-mx-75',
          'xl:col-start-2 xl:col-span-8 xl:-mx-45',
          'xxl:col-start-4'
        )}
      >
        <div className={classNames('flex flex-col my-30 mx-30 lg:mx-60 lg:my-60')}>
          <div
            className={classNames('flex flex-col')}
            onClick={() => setExpanded(current => !current)}
          >
            <div className={classNames('flex flex-row items-center')}>
              {!isNullOrWhiteSpace(title) && (
                <h2 className="font-h3 text-h3 text-secondary-darker flex-1">{title}</h2>
              )}

              <AccordionButton
                additionalClassName="lg:hidden"
                iconColor="text-secondary-darker"
                active={expanded}
              />
            </div>

            {!isNullOrWhiteSpace(text) && (
              <Paragraph className="font-body text-body text-secondary-darker">
                {text}
              </Paragraph>
            )}
          </div>

          <KeyDocumentTable
            downloadsGaTrackingId={downloadsGaTrackingId}
            downloadText={translations.downloadFile ?? 'Download file'}
            rows={rows}
            tableHeadings={tableHeadings}
          />

          <Collapse
            className="transition-all duration-200"
            contentClassName="flex pt-20 tb:justify-end lg:hidden "
            isOpen={expanded}
          >
            <KeyDocumentAccordion
              downloadText={translations.downloadFile ?? 'Download file'}
              rows={rows}
              tableHeadings={tableHeadings}
            />
          </Collapse>
        </div>
      </div>
    </MarketSection>
  )
}

const DownloadLink = ({
  attachment,
  gaTrackingId,
  linkText,
}: {
  attachment: Attachment
  gaTrackingId?: string
  linkText: string
}) => (
  <div>
    <a
      className={classNames(
        'rounded-20 border border-secondary text-secondary inline-flex gap-5 items-center',
        'text-t-400  font-t-bold whitespace-nowrap',
        'py-5 px-15',
        'transition-hover hover:bg-secondary hover:text-white'
      )}
      download
      data-ga-tracking-id={gaTrackingId}
      data-ga-tracking-name={
        !isNullOrWhiteSpace(gaTrackingId) ? attachment.title : undefined
      }
      href={attachment.url}
      title={attachment.title}
    >
      {linkText}

      <ArrowIcon
        width="45"
        height="45"
        className="fill-current w-10 h-10 xl:w-10 xl:h-15 xxl:w-15 xxl:h-20 mt-5"
      />
    </a>
  </div>
)

export interface KeyDocumentTableProps {
  downloadsGaTrackingId?: string
  downloadText: string
  rows?: KeyDocumentRow[]
  tableHeadings?: string[]
}

const KeyDocumentTable = ({
  downloadsGaTrackingId,
  downloadText,
  rows,
  tableHeadings,
}: KeyDocumentTableProps) => (
  <table className={classNames('hidden lg:table mt-30 table-auto')}>
    {!!tableHeadings && (
      <thead className={classNames('w-full')}>
        <tr>
          {tableHeadings.map((th, thIndex) => (
            <th
              className={classNames(
                'border-solid border-b border-neutral-lighter',
                'font-body text-t-400 text-secondary-darker whitespace-nowrap',
                'text-left py-20 px-20',
                { 'w-full': thIndex === 0 }
              )}
              key={thIndex}
            >
              {th}
            </th>
          ))}
        </tr>
      </thead>
    )}

    {!!rows && (
      <tbody className={classNames('w-full')}>
        {rows.map((tr, trIndex) => (
          <tr
            key={trIndex}
            className={classNames('border-solid border-b border-neutral-lighter ')}
          >
            {!!tr.cells &&
              tr.cells.map((td, tdIndex) => (
                <td
                  className={classNames(
                    'text-body text-secondary-darker',
                    'py-20 px-20',
                    'min-w-330 align-top',
                    {
                      'text-400': tdIndex === 0,
                      'font-body': tdIndex !== 0,
                      'w-full': tdIndex === 0,
                    }
                  )}
                  key={tdIndex}
                >
                  {td ?? '-'}
                </td>
              ))}

            {!!tr.attachments && tr.attachments.length > 0 && (
              <td>
                <div className={classNames('flex flex-col gap-15')}>
                  {tr.attachments.map((attachment: Attachment, attachmentIndex) => (
                    <DownloadLink
                      attachment={attachment}
                      gaTrackingId={downloadsGaTrackingId}
                      key={attachmentIndex}
                      linkText={downloadText}
                    />
                  ))}
                </div>
              </td>
            )}

            {!!tr.secondaryAttachments && tr.secondaryAttachments.length > 0 && (
              <td>
                <div className={classNames('flex flex-col gap-15')}>
                  {tr.secondaryAttachments.map(
                    (attachment: Attachment, attachmentIndex) => (
                      <DownloadLink
                        attachment={attachment}
                        gaTrackingId={downloadsGaTrackingId}
                        key={attachmentIndex}
                        linkText={downloadText}
                      />
                    )
                  )}
                </div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    )}
  </table>
)

const KeyDocumentAccordion = ({
  downloadsGaTrackingId,
  tableHeadings,
  rows,
  downloadText,
}: KeyDocumentTableProps) => (
  <div className={classNames('flex w-full flex-col lg:hidden')}>
    {!!rows && (
      <ul>
        {rows.map((row, rowIndex) => (
          <li
            key={rowIndex}
            className={classNames(
              'py-20 border-solid border-t border-neutral-lighter gap-15 flex flex-col'
            )}
          >
            {!!row.cells &&
              row.cells.map((cell, cellIndex) => {
                if (!!cell) {
                  return (
                    <div
                      key={`${rowIndex}-${cellIndex}`}
                      className={classNames('gap-5 flex flex-col')}
                    >
                      {!!tableHeadings &&
                        tableHeadings.length >= cellIndex &&
                        !!tableHeadings[cellIndex] && (
                          <h3 className="font-body text-t-400 text-secondary-darker">
                            {tableHeadings[cellIndex]}
                          </h3>
                        )}

                      <Paragraph className="text-400 text-t-500 text-secondary-darker">
                        {cell}
                      </Paragraph>
                    </div>
                  )
                }

                return undefined
              })}

            {!!row.attachments && row.attachments.length > 0 && (
              <div className={classNames('gap-5 flex flex-col')}>
                <h3 className="font-body text-t-400 text-secondary-darker">
                  {!!tableHeadings &&
                    tableHeadings[
                      tableHeadings?.length -
                        (!!row.secondaryAttachments &&
                        row.secondaryAttachments.length > 0 &&
                        tableHeadings.length > 1
                          ? 2
                          : 1)
                    ]}
                </h3>

                <div className={classNames('flex flex-col gap-15')}>
                  {row.attachments.map((attachment, attachmentIndex) => (
                    <DownloadLink
                      attachment={attachment}
                      gaTrackingId={downloadsGaTrackingId}
                      key={attachmentIndex}
                      linkText={downloadText}
                    />
                  ))}
                </div>
              </div>
            )}

            {!!row.secondaryAttachments && row.secondaryAttachments.length > 0 && (
              <div className={classNames('gap-5 flex flex-col')}>
                <h3 className="font-body text-t-400 text-secondary-darker">
                  {!!tableHeadings && tableHeadings[tableHeadings?.length - 1]}
                </h3>

                <div className={classNames('flex flex-col gap-15')}>
                  {row.secondaryAttachments.map((attachment, attachmentIndex) => (
                    <DownloadLink
                      attachment={attachment}
                      gaTrackingId={downloadsGaTrackingId}
                      key={attachmentIndex}
                      linkText={downloadText}
                    />
                  ))}
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
)
